/**
 * 北海道 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 選定理由と、書いてはいけないことは claude/北海道データシート案.md を参照。
 * とくに次の3点はデータにも反映している。
 *  - Lv1〜3 は「その形のまま売られ、食べられているもの」に限る
 *  - 「発祥」は公式資料が断定していない限り書かない（ザンギ・味噌ラーメン）
 *  - 企業の登録商標は使わず一般名にする（バターサンド）
 *
 * 本番画像は public/assets/foods/hokkaido/ に置く。
 * ファイルが無い場合は自動的に仮アイコンで動作する。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：夕張メロン・バターサンド（店で食べられる品に差し替え）
 * - じゃがバター：「じゃがバター発祥の地」「塩辛じゃがバターは函館発祥」：国・自治体・公式観光協会の資料で確かめられなかったので書かない（ネット上の記事には書かれているが、ブログ・店の記事のみ）
 * - じゃがバター：十字に切れ目を入れる・男爵いもを使う、など作り方の細部：出典ページに書かれていないので書かない（絵には十字の切れ目があるが、説明文では触れない）
 * - じゃがバター：男爵いもの由来（明治41年に川田龍吉男爵が函館近郊で栽培させた）：北海道農政事務所のページにあるが、じゃがバターの説明ではないので使わなかった（使うなら https://www.maff.go.jp/hokkaido/suishin/shokuiku/hokkaido_dentoyasai.html を tipSource にできる）
 * - 室蘭やきとり：元祖の店名（農水省ページに昭和12年創業の店が「元祖とされる」とある）：特定の店名は書かない決まりのため書かない
 * - 室蘭やきとり：「日本三大やきとり」：ふるさと納税サイト等にはあるが、農水省・北海道観光機構・室蘭やきとりの会のページでは確かめられなかったので書かない
 * - 室蘭やきとり：文化庁「100年フード」認定（2026年2月）：室蘭やきとりの会のサイトと報道にはあるが、文化庁のページを開いて確かめていないので書かない（入れるなら文化庁の認定一覧で確認してから）
 * - 室蘭やきとり：店の数（「人口あたりの店舗数が全国有数」）：北海道観光機構のページにあるが、数字の裏付けが示されていないので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { HOKKAIDO_FOOD_HULLS } from './hokkaidoFoodShapes.generated';
import { hokkaidoSceneryTour } from './hokkaidoScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'soft-cream',
    level: 1,
    name: 'ソフトクリーム',
    shortName: 'ソフト',
    image: '/assets/foods/hokkaido/01_soft_cream.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '牧場の生乳でつくるソフトクリーム。',
    source: 'https://www.maff.go.jp/j/kanbo/tiho/attach/pdf/todouhuken_gaiyou-107.pdf',
    icon: { label: 'ソフト', shape: 'mochi', baseColor: '#f7f0e2', accentColor: '#d8a84a' },
    reward: {
      unlockScore: 200,
      area: '道内全域',
      headline: '生乳の生産量は全国の約56%。牧場のそばで食べるのがいちばん。',
      tip: '道の駅や牧場の直売所に必ずある。濃さは牧場ごとに違うので飲み比べならぬ食べ比べを。',
      links: searchLinks('北海道', 'ソフトクリーム 牧場'),
    },
  },
  {
    id: 'jaga-butter',
    level: 2,
    name: 'じゃがバター',
    shortName: 'じゃがバター',
    image: '/assets/foods/hokkaido/02_jaga_butter.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'ふかしたじゃがいもにバターをのせる。函館では、ふかしたじゃがいもにいかの塩辛を合わせる食べ方がもっとも人気があるとされ、バターを加えた「塩辛じゃがバター」も北海道らしい味として紹介されている。',
    source:
      'https://www.hakobura.jp/features/24',
    icon: { label: 'じゃがバター', shape: 'mochi', baseColor: '#e8cf7a', accentColor: '#a8743a' },
    reward: {
      unlockScore: 500,
      area: '函館市',
      headline: 'ほくほくのいもに塩辛とバター',
      tip: 'のせる塩辛は、いかの身とゴロ（内臓）に塩などを加えて熟成させたもの。いかを無駄なく食べる知恵から生まれ、函館では「いか売り」の文化とともに発展した。',
      links: searchLinks('函館市', '塩辛じゃがバター'),
      official: [{ label: '函館市公式観光情報 はこぶら（いかの塩辛特集）', url: 'https://www.hakobura.jp/features/24' }],
    },
  },
  {
    id: 'imomochi',
    level: 3,
    name: 'いももち',
    shortName: 'いももち',
    image: '/assets/foods/hokkaido/03_imomochi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'じゃがいもでつくる焼き餅。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/imomochi_hokkaido.html',
    icon: { label: '芋餅', shape: 'feast', baseColor: '#e8d5a8', accentColor: '#a9762f' },
    reward: {
      unlockScore: 1000,
      area: '道内全域',
      headline: 'もち米の代わりに、豊富にとれたじゃがいもで餅をついたのがはじまりとされる。',
      tip: '甘じょっぱい醤油だれで香ばしく焼く。「いもだんご」とも呼ばれる郷土料理。',
      links: searchLinks('北海道', 'いももち'),
    },
  },
  {
    id: 'muroran-yakitori',
    level: 4,
    name: '室蘭やきとり',
    shortName: '室蘭やきとり',
    image: '/assets/foods/hokkaido/04_muroran_yakitori.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'ひと口大の豚肩ロースとくし切りのたまねぎを交互に串に刺し、炭で焼いてたれにくぐらせ、もう一度焼く。洋がらしを添えて食べる。名は「やきとり」だが、鶏肉ではなく豚肉を使う。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/muroranyakitori_hokkaido.html',
    icon: { label: '豚串', shape: 'feast', baseColor: '#8a4a25', accentColor: '#e8c53a' },
    reward: {
      unlockScore: 1800,
      area: '室蘭市',
      headline: 'やきとりなのに豚とたまねぎ',
      tip: '長ねぎではなくたまねぎを使うのは、北海道産のたまねぎが安く、豚肉と相性がよかったため。市内の学校給食には「室蘭やきとり丼」も出されている。',
      links: searchLinks('室蘭市', '室蘭やきとり'),
      official: [{ label: '北海道観光機構 HOKKAIDO LOVE!「室蘭やきとり」', url: 'https://www.visit-hokkaido.jp/spot/detail_12815.html' }, { label: '室蘭やきとりの会（室蘭商工会議所まちづくりファンド補助事業）', url: 'https://muroran-yakitori.net/' }],
    },
  },
  {
    id: 'zangi',
    level: 5,
    name: 'ザンギ',
    shortName: 'ザンギ',
    image: '/assets/foods/hokkaido/05_zangi.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '下味をしっかりつけた鶏の唐揚げ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/zangi_hokkaido.html',
    icon: { label: 'ザンギ', shape: 'dumpling', baseColor: '#c98a3c', accentColor: '#4a3524' },
    reward: {
      unlockScore: 3000,
      area: '釧路市',
      headline:
        '昭和30年ごろ、釧路の鶏料理店が鶏一羽をぶつ切りにして揚げたのがはじまりといわれる。',
      tip: '釧路ではウスターソースをかけて食べる店もある。下味が濃いのでそのままでも。',
      links: searchLinks('釧路市', 'ザンギ'),
    },
  },
  {
    id: 'soup-curry',
    level: 6,
    name: 'スープカレー',
    shortName: 'スープカレー',
    image: '/assets/foods/hokkaido/06_soup_curry.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'さらりとしたスープ状のカレー。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_hokkaido.html',
    icon: { label: 'カレー', shape: 'curry-plate', baseColor: '#c8701f', accentColor: '#6fa84f' },
    reward: {
      unlockScore: 4500,
      area: '札幌市',
      headline: '札幌生まれ。文化庁の「100年フード」にも認定されています。',
      tip: '骨付きチキンと素揚げの野菜が定番。辛さとライスの量を選べる店が多い。',
      links: searchLinks('札幌市', 'スープカレー'),
    },
  },
  {
    id: 'sapporo-miso-ramen',
    level: 7,
    name: '札幌味噌ラーメン',
    shortName: '味噌ラーメン',
    image: '/assets/foods/hokkaido/07_miso_ramen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'ちぢれ麺の味噌ラーメン。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ramen_hokkaido.html',
    icon: { label: '味噌', shape: 'ramen', baseColor: '#d9a552', accentColor: '#7a3b1e' },
    reward: {
      unlockScore: 6500,
      area: '札幌市',
      headline: '札幌は味噌、函館は塩、旭川は醤油。北海道のラーメンは街ごとに違う。',
      tip: 'スープの表面をラードが覆うので最後まで熱い。猫舌の人は気をつけて。',
      links: searchLinks('札幌市', '味噌ラーメン'),
    },
  },
  {
    id: 'butadon',
    level: 8,
    name: '豚丼',
    shortName: '豚丼',
    image: '/assets/foods/hokkaido/08_butadon.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '甘辛いたれで焼いた豚肉の丼。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/butadon_hokkaido.html',
    icon: { label: '豚丼', shape: 'rice-bowl', baseColor: '#c0692c', accentColor: '#4d3018' },
    reward: {
      unlockScore: 9000,
      area: '帯広市・十勝',
      headline: '養豚が盛んだった十勝・帯広が発祥の地といわれる。',
      tip: '炭火で焼いてたれをからめるのが基本。肉の厚さは店ごとにかなり違う。',
      links: searchLinks('帯広市 十勝', '豚丼'),
    },
  },
  {
    id: 'jingisukan',
    level: 9,
    name: 'ジンギスカン',
    shortName: 'ジンギスカン',
    image: '/assets/foods/hokkaido/09_jingisukan.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '専用の鉄鍋で焼く羊肉。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/jingisukan_hokkaido.html',
    icon: { label: '羊', shape: 'hotpot', baseColor: '#8f5a3c', accentColor: '#6d9a3f' },
    reward: {
      unlockScore: 12000,
      area: '道内全域',
      headline: '羊肉の消費を広げるために根づいた料理。2004年に北海道遺産にも選ばれた。',
      tip: 'たれに漬けてから焼く「味付け」と、焼いてからつける「後付け」の二系統がある。',
      links: searchLinks('北海道', 'ジンギスカン'),
    },
  },
  {
    id: 'ishikari-nabe',
    level: 10,
    name: '石狩鍋',
    shortName: '石狩鍋',
    image: '/assets/foods/hokkaido/10_ishikari_nabe.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '鮭を味噌仕立てにした鍋。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ishikarinabe_hokkaido.html',
    icon: { label: '石狩', shape: 'pot', baseColor: '#e7e0cd', accentColor: '#c2452f' },
    reward: {
      unlockScore: 16000,
      area: '石狩市',
      headline: '鮭で知られる石狩川の河口、石狩の漁師料理から生まれたとされる。',
      tip: '仕上げに山椒をひと振り。文化庁の「100年フード」にも認定されている。',
      links: searchLinks('石狩市', '石狩鍋'),
    },
  },
  {
    id: 'uni-ikura-don',
    level: 11,
    name: 'ウニいくら丼',
    shortName: 'ウニいくら丼',
    image: '/assets/foods/hokkaido/11_uni_ikura_don.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'ウニといくらを敷きつめた海鮮丼。',
    source: 'https://www.visit-hokkaido.jp/gourmet/kaisen',
    icon: { label: '海鮮', shape: 'noodle-bowl', baseColor: '#f0e6d2', accentColor: '#e2622f' },
    reward: {
      unlockScore: 22000,
      area: '積丹・小樽',
      headline: '北海道観光振興機構によると、北海道はウニの水揚げ量が日本一。積丹のウニは夏、いくらは秋が旬。',
      tip: '積丹のウニは6月から8月の限定。いくらは鮭が揚がる9月から11月。',
      links: searchLinks('積丹 小樽', 'ウニいくら丼'),
    },
  },
  {
    id: 'hamayude-kegani',
    level: 12,
    name: '浜ゆで毛ガニ',
    shortName: '毛ガニ',
    image: '/assets/foods/hokkaido/12_kegani.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '水揚げしたその場でゆで上げる、北海道の冬のごちそう。',
    source: 'https://www.pref.hokkaido.lg.jp/sr/gid/fis080.html',
    icon: {
      label: '毛蟹',
      shape: 'sashimi-platter',
      baseColor: '#c14a2c',
      accentColor: '#f4e6d2',
    },
    reward: {
      unlockScore: 30000,
      area: 'オホーツク・道東',
      headline: 'オホーツク海と太平洋側に多く分布。甲長8cm以上の雄しか獲らない決まりがある。',
      tip: 'まずは何もつけずに脚から。かにみそは甲羅のまま温めるとよりおいしい。',
      links: searchLinks('オホーツク 道東', '毛ガニ'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const hokkaidoFoodStage: StageDefinition = {
  regionId: 'hokkaido',
  regionName: '北海道',
  mode: 'food',
  title: '北海道めしマージ',
  theme: {
    primaryColor: '#1f4e79',
    secondaryColor: '#4a90c2',
    accentColor: '#f2c14e',
    backgroundColor: '#16222b',
  },
  /**
   * 背景ツアー「北海道めぐり」。軸は一日の時間帯ではなく季節。
   * 時間帯写真（photos）は用意していないので、手続き生成の背景が下敷きになる。
   */
  scenery: {
    tour: hokkaidoSceneryTour,
  },
  // 画像の輪郭（自動生成）を付与する。輪郭がある料理は丸以外の当たり判定になる。
  items: attachHulls(FOOD_ITEMS, HOKKAIDO_FOOD_HULLS),
};
